import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, ArrowRight, Tag, LayoutGrid, Loader2 } from "lucide-react";
import ProductImage from "@/components/ProductImage";
import { fetchProducts } from "@/lib/api";
import { suggest } from "@/lib/search";
import { getBrand, getCategory, displayPrice, formatPrice } from "@/data/catalog";

let productsPromise = null;
const loadProducts = () => (productsPromise ||= fetchProducts().catch(() => { productsPromise = null; return []; }));

function useSuggestions(q) {
  const [list, setList] = useState(null);
  useEffect(() => { loadProducts().then(setList); }, []);
  const [debounced, setDebounced] = useState(q);
  useEffect(() => { const t = setTimeout(() => setDebounced(q), 120); return () => clearTimeout(t); }, [q]);
  const data = useMemo(() => (list ? suggest(list, debounced) : null), [list, debounced]);
  return { data, loading: list === null };
}

export default function SearchBar({ initial = "", size = "lg", testId = "search" }) {
  const [q, setQ] = useState(initial);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [hover, setHover] = useState(-1);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { data, loading } = useSuggestions(q);

  useEffect(() => setQ(initial), [initial]);
  useEffect(() => {
    const close = (e) => wrapRef.current && !wrapRef.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const items = useMemo(() => {
    if (!data) return [];
    const out = [];
    if (data.combo) {
      const b = getBrand(data.combo.brand), c = getCategory(data.combo.category);
      out.push({ kind: "combo", key: "combo", label: data.combo.multi ? q.trim().replace(/\b\w/g, (m) => m.toUpperCase()) : `${b?.name} ${c?.name}`, to: `/shop?q=${encodeURIComponent(q.trim())}` });
    }
    data.brands.forEach((b) => out.push({ kind: "brand", key: `b-${b.slug}`, label: b.name, slug: b.slug, to: `/brand/${b.slug}` }));
    data.categories.forEach((c) => out.push({ kind: "category", key: `c-${c.slug}`, label: c.name, slug: c.slug, to: `/category/${c.slug}` }));
    data.products.forEach((p) => out.push({ kind: "product", key: `p-${p.id}`, product: p, to: `/product/${p.slug}` }));
    return out;
  }, [data, q]);

  const go = (to) => { setOpen(false); setActive(-1); navigate(to); };
  const submit = (e) => {
    e.preventDefault();
    if (active >= 0 && items[active]) return go(items[active].to);
    go(q.trim() ? `/shop?q=${encodeURIComponent(q.trim())}` : "/shop");
  };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((i) => Math.min(i + 1, items.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, -1)); }
    else if (e.key === "Escape") { setOpen(false); setActive(-1); }
  };

  const big = size === "lg";
  const showPanel = open && q.trim().length > 0;

  return (
    <div ref={wrapRef} className="relative w-full" data-testid={`${testId}-wrapper`}>
      <form onSubmit={submit} role="search" className={`flex items-center gap-2 rounded-md bg-white shadow-[0_20px_50px_-20px_rgba(0,0,0,0.6)] ring-1 ring-slate-200 transition-shadow duration-200 focus-within:ring-2 focus-within:ring-sky-400 ${big ? "p-2" : "p-1"}`}>
        <Search className={`ml-2 shrink-0 text-slate-500 ${big ? "h-5 w-5" : "h-4 w-4"}`} />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(-1); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder="Search brand, category, product or model #"
          aria-label="Search equipment"
          autoComplete="off"
          className={`min-w-0 flex-1 bg-transparent text-slate-900 placeholder:text-slate-500 focus:outline-none ${big ? "h-12 text-base" : "h-9 text-sm"}`}
          data-testid={`${testId}-input`}
        />
        {q && (
          <button type="button" onClick={() => { setQ(""); setActive(-1); inputRef.current?.focus(); }} className="rounded-sm p-1 text-slate-400 hover:text-slate-700" aria-label="Clear search" data-testid={`${testId}-clear`}>
            <X className="h-4 w-4" />
          </button>
        )}
        <button type="submit" className={`shrink-0 rounded-sm bg-sky-600 font-semibold text-white transition-colors duration-150 hover:bg-sky-500 ${big ? "h-12 px-5 text-sm" : "h-9 px-3 text-xs"}`} data-testid={`${testId}-submit`}>
          Search
        </button>
      </form>

      {showPanel && (
        <SuggestPanel items={items} active={active} hover={hover} loading={loading} q={q} onGo={go} onHover={setHover} testId={testId} />
      )}
    </div>
  );
}

function SuggestPanel({ items, active, hover, loading, q, onGo, onHover, testId }) {
  return (
    <div onMouseLeave={() => onHover(-1)} className="absolute left-0 right-0 top-full z-50 mt-2 max-h-[50vh] sm:max-h-[70vh] overflow-y-auto rounded-md border border-slate-200 bg-white text-slate-900 shadow-2xl" data-testid={`${testId}-dropdown`}>
      {loading ? (
        <div className="flex items-center gap-2 p-4 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading catalog…</div>
      ) : items.length === 0 ? (
        <div className="p-4 text-sm text-slate-600" data-testid={`${testId}-no-results`}>
          No matches for <span className="font-semibold">“{q.trim()}”</span>. Try a brand (Goodman, Daikin), a category (furnaces, coils) or a model number.
        </div>
      ) : (
        <ul role="listbox">
          {items.map((it, i) => (
            <li key={it.key} role="option" aria-selected={i === active}>
              <button
                type="button"
                onMouseEnter={() => onHover(i)}
                onClick={() => onGo(it.to)}
                className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors duration-100 ${i === active || i === hover ? "bg-sky-50" : ""} ${i > 0 && items[i - 1].kind !== "product" && it.kind === "product" ? "border-t border-slate-100" : ""}`}
                data-testid={`${testId}-suggestion-${it.kind}-${it.slug || it.product?.slug || "all"}`}
              >
                {it.kind === "product" ? <ProductRow p={it.product} /> : <ShortcutRow it={it} />}
              </button>
            </li>
          ))}
          <li className="border-t border-slate-100">
            <button type="button" onClick={() => onGo(`/shop?q=${encodeURIComponent(q.trim())}`)} className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-sky-700 hover:bg-slate-50" data-testid={`${testId}-view-all`}>
              View all results for “{q.trim()}” <ArrowRight className="h-4 w-4" />
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}

function ShortcutRow({ it }) {
  const Icon = it.kind === "category" ? LayoutGrid : Tag;
  const tag = it.kind === "brand" ? "Brand" : it.kind === "category" ? "Category" : "Brand + Category";
  return (
    <>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm bg-slate-100 text-sky-700"><Icon className="h-4 w-4" /></span>
      <span className="flex-1 truncate text-sm font-semibold">{it.label}</span>
      <span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{tag}</span>
    </>
  );
}

function ProductRow({ p }) {
  const dp = displayPrice(p);
  return (
    <>
      <span className="h-11 w-11 shrink-0 overflow-hidden rounded-sm border border-slate-100 bg-white"><ProductImage product={p} showStamp={false} /></span>
      <span className="min-w-0 flex-1">
        <span className="line-clamp-1 text-sm font-semibold text-slate-900">{p.name}</span>
        <span className="block truncate font-mono text-[11px] text-slate-500">{getBrand(p.brandSlug)?.name} · {p.model}</span>
      </span>
      <span className="shrink-0 text-right font-mono text-xs font-semibold text-slate-700">{dp.mode === "show" ? formatPrice(dp.price) : "Request Price"}</span>
    </>
  );
}
